/**
 * Shared building blocks for the 2026-10 redesign (prototype: docs/design/prototype-2026-10/*.dc.html).
 * Every page of phase 2 is built from these; they only use design tokens (styles/tokens.css) and kit.css.
 *
 *   <Page>                    centred 960px column (the prototype ".wrap")
 *   <PageTitle>               small eyebrow line + big h1 (+ optional back link / actions)
 *   <Card>                    white box, 14px radius, optional tone (peach notice / sand)
 *   <StepList>                numbered steps: done ✓ / current / todo, each with title, one-line text, optional link
 *   <Pill>                    rounded label in four tones: peach, blue, green, sand
 *   <Notice>                  peach reminder bar with ONE secondary action
 *   <CompanyRow>              one line of "your other companies": mark, name, status pill, next action
 *   <BigAmount>               large euro amount with a label above and one line of explanation below
 *   <CompanyMark>             square initials mark of a company (fixed colour per company)
 *   <BrandLink>               logo + "BTW Kwartier" (+ 一刻报税 in Chinese)
 *
 * One primary button per page: none of these components renders a primary button on its own.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import type { EntityRefDTO } from '@btw/shared';
import { useI18n } from '../i18n';
import { buttonClass } from './Button';
import { LogoMark } from './Logo';
import '../styles/kit.css';

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ');

/* ───────── Actions ───────── */

/** A link-like action: `to` (in-app route), `href` (external, new tab) or `onClick`. */
export interface KitAction {
  label: ReactNode;
  to?: string;
  href?: string;
  onClick?: () => void;
  /** Accessible name when the visible label is short or ambiguous ("Bekijken"). */
  ariaLabel?: string;
}

function ActionEl({ action, className }: { action: KitAction; className: string }) {
  if (action.to)
    return (
      <Link to={action.to} className={className} aria-label={action.ariaLabel}>
        {action.label}
      </Link>
    );
  if (action.href)
    return (
      <a href={action.href} className={className} target="_blank" rel="noreferrer noopener" aria-label={action.ariaLabel}>
        {action.label}
      </a>
    );
  return (
    <button type="button" className={className} onClick={action.onClick} aria-label={action.ariaLabel}>
      {action.label}
    </button>
  );
}

/* ───────── Page & title ───────── */

export function Page({ children, wide, className }: { children: ReactNode; wide?: boolean; className?: string }) {
  return <div className={cx('kit-page', wide && 'kit-page--wide', className)}>{children}</div>;
}

/**
 * Page heading as in the prototype: muted eyebrow ("2026 · Q3 · 1 jul – 30 sep"), then the h1.
 * `back` renders "← Terug naar …" above it; `actions` sit on the right (secondary buttons only).
 */
export function PageTitle({ eyebrow, title, back, actions, id }: { eyebrow?: ReactNode; title: ReactNode; back?: KitAction; actions?: ReactNode; id?: string }) {
  return (
    <header className="kit-title">
      {back && (
        <ActionEl
          action={{ ...back, label: (
            <>
              <ArrowLeft size={16} aria-hidden="true" /> {back.label}
            </>
          ) }}
          className="kit-title__back"
        />
      )}
      <div className="kit-title__row">
        <div className="kit-title__text">
          {eyebrow && <div className="kit-title__eyebrow">{eyebrow}</div>}
          <h1 className="kit-title__h1" id={id}>
            {title}
          </h1>
        </div>
        {actions && <div className="kit-title__actions">{actions}</div>}
      </div>
    </header>
  );
}

/* ───────── Card ───────── */

export type CardTone = 'default' | 'peach' | 'sand';

export function Card({
  children,
  pad = 'md',
  tone = 'default',
  as: Tag = 'section',
  className,
  ...aria
}: {
  children: ReactNode;
  /** none = flush lists (rows bring their own padding); md = 22px; lg = 32px (hero cards). */
  pad?: 'none' | 'md' | 'lg';
  tone?: CardTone;
  as?: 'section' | 'div' | 'article' | 'aside';
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}) {
  return (
    <Tag className={cx('kit-card', `kit-card--pad-${pad}`, tone !== 'default' && `kit-card--${tone}`, className)} {...aria}>
      {children}
    </Tag>
  );
}

/* ───────── Pill ───────── */

export type PillTone = 'peach' | 'blue' | 'green' | 'sand';

/** Status/label pill. Colour never carries meaning alone: the text says it (WCAG 1.4.1). */
export function Pill({ tone = 'sand', icon, children, className }: { tone?: PillTone; icon?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <span className={cx('kit-pill', `kit-pill--${tone}`, className)}>
      {icon}
      {children}
    </span>
  );
}

/* ───────── StepList ───────── */

export type StepState = 'done' | 'current' | 'todo';

export interface StepItem {
  key: string;
  title: ReactNode;
  /** One line under the title, e.g. "ING-afschrift dekt het hele kwartaal · 13 facturen". */
  description?: ReactNode;
  state: StepState;
  /** Small text link on the right ("Bekijken", "Wijzigen"). */
  action?: KitAction;
  /** Muted text on the right instead of a link ("17 stuks"). */
  meta?: ReactNode;
}

/**
 * Numbered steps (the three steps of a quarter, onboarding, submit). Done = green ✓, current = filled orange number
 * with "· nu", todo = sand number. The state is also spoken for screen readers.
 */
export function StepList({ steps, label, compact }: { steps: StepItem[]; label: string; compact?: boolean }) {
  const { t } = useI18n();
  return (
    <ol className={cx('kit-steps', compact && 'kit-steps--compact')} aria-label={label}>
      {steps.map((s, i) => (
        <li key={s.key} className="kit-steps__item" data-state={s.state} aria-current={s.state === 'current' ? 'step' : undefined}>
          <span className="kit-steps__num" aria-hidden="true">
            {s.state === 'done' ? <Check size={16} strokeWidth={2.5} /> : i + 1}
          </span>
          <div className="kit-steps__body">
            <div className="kit-steps__title">
              {s.title}
              {s.state === 'current' && <span className="kit-steps__now"> · {t('kit.step.now')}</span>}
              <span className="visually-hidden"> ({t(`kit.step.${s.state}`)})</span>
            </div>
            {s.description && <div className="kit-steps__desc">{s.description}</div>}
          </div>
          {s.action ? <ActionEl action={s.action} className="kit-steps__action" /> : s.meta ? <span className="kit-steps__meta">{s.meta}</span> : null}
        </li>
      ))}
    </ol>
  );
}

/* ───────── Notice ───────── */

/** Peach reminder bar: one sentence (bold start allowed) and at most one secondary button. Not for errors. */
export function Notice({ children, action, icon }: { children: ReactNode; action?: KitAction; icon?: ReactNode }) {
  return (
    <div className="kit-notice">
      {icon && <span className="kit-notice__icon">{icon}</span>}
      <div className="kit-notice__text">{children}</div>
      {action && <ActionEl action={action} className={buttonClass({ variant: 'secondary', size: 'sm' }, 'kit-notice__action')} />}
    </div>
  );
}

/* ───────── Company ───────── */

export function CompanyMark({ entity, size = 'md' }: { entity: Pick<EntityRefDTO, 'color' | 'initials'>; size?: 'sm' | 'md' | 'lg' }) {
  return (
    <span className={cx('kit-mark', `kit-mark--${size}`)} style={{ ['--mark-color' as string]: entity.color }} aria-hidden="true">
      {entity.initials}
    </span>
  );
}

/**
 * One row of the "other companies" list: mark, name, status pill and the next action. Clicking the name calls
 * `onSelect` (switch to that company). Rows stack inside a `<Card pad="none">`.
 */
export function CompanyRow({
  entity,
  pill,
  action,
  onSelect,
}: {
  entity: EntityRefDTO;
  pill?: { tone: PillTone; label: ReactNode };
  action?: KitAction;
  onSelect?: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="kit-company-row">
      <CompanyMark entity={entity} />
      {onSelect ? (
        <button type="button" className="kit-company-row__name kit-company-row__name--btn" onClick={onSelect} aria-label={t('company.switchTo', { name: entity.name })}>
          {entity.name}
        </button>
      ) : (
        <span className="kit-company-row__name">{entity.name}</span>
      )}
      {pill && <Pill tone={pill.tone}>{pill.label}</Pill>}
      {action ? <ActionEl action={action} className="kit-company-row__action" /> : <span className="kit-company-row__action" aria-hidden="true" />}
    </div>
  );
}

/* ───────── BigAmount ───────── */

/**
 * The amount of the quarter. `euro` is whole euros as the rule engine returns it: positive = te betalen,
 * negative = te ontvangen (shown without a minus — the label says the direction), null = not computed yet ("—").
 */
export function BigAmount({ label, euro, note, size = 'lg', id }: { label: ReactNode; euro: number | null; note?: ReactNode; size?: 'md' | 'lg'; id?: string }) {
  const { t, fmt } = useI18n();
  return (
    <div className={cx('kit-amount', `kit-amount--${size}`)}>
      <div className="kit-amount__label" id={id}>
        {label}
      </div>
      <div className="kit-amount__value money">
        {euro === null ? (
          <>
            <span aria-hidden="true">—</span>
            <span className="visually-hidden">{t('kit.amount.none')}</span>
          </>
        ) : (
          fmt.euro(Math.abs(euro))
        )}
      </div>
      {note && <div className="kit-amount__note">{note}</div>}
    </div>
  );
}

/* ───────── Brand ───────── */

/** Logo + word mark as in the prototype header; Chinese UI adds 一刻报税 (desktop only). */
export function BrandLink({ to = '/', label }: { to?: string; label: string }) {
  const { locale } = useI18n();
  return (
    <Link to={to} className="brand-link" aria-label={label}>
      <LogoMark size={28} />
      <span className="brand-link__name" aria-hidden="true" translate="no">
        <b>BTW</b> Kwartier
      </span>
      {locale === 'zh' && (
        <span className="brand-link__sub" aria-hidden="true">
          一刻报税
        </span>
      )}
    </Link>
  );
}
